import { checkIfImageExists } from "@/utils/check-if-image-exists";
import { getUploadDir, type UploadDirParams } from "@/utils/get-upload-dir";
import { s3Url } from "@/utils/s3-prefix";
import { FileWithMetadata } from ".";

function delayedCb(url: string, cb: (err: Error | null) => void) {
	return (e: Error | null) =>
		e
			? cb(e)
			: checkIfImageExists({ url, timeout: 50000 }, (exists: boolean | null) =>
					cb(exists ? null : new Error("Failed to create versions")),
				);
}

/**
 * Sanitize filename for S3 URL - must match the sanitization in upload-service-apollo.tsx
 */
export function sanitizeFileName(fileName: string): string {
	return fileName
		.replace(/\s+/g, "_") // Replace spaces with underscores
		.replace(/[^a-zA-Z0-9._-]/g, ""); // Remove special characters except . _ -
}

const getImageSrc = (name: string, uploadDirDoc: UploadDirParams) => {
	const uploadDir = getUploadDir(uploadDirDoc);
	// Sanitize the filename to match how files are stored in S3
	const sanitizedName = sanitizeFileName(name);
	return s3Url(`${uploadDir}${sanitizedName}`);
};

const isImageFile = (fileName: string): boolean => {
	const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp", ".svg", ".ico", ".tiff"];
	const extension = fileName.toLowerCase().substring(fileName.lastIndexOf("."));
	return imageExtensions.includes(extension);
};

const organizeFilesByType = (files: FileWithMetadata[]) => {
	const images: FileWithMetadata[] = [];
	const documents: FileWithMetadata[] = [];

	files.forEach(f => {
		if (isImageFile(f.name)) images.push(f);
		else documents.push(f);
	});

	return { images, documents };
};

function getFileExtension(fileObj: FileWithMetadata, label?: boolean): string {
	let extension;
	if (fileObj.type) {
		// Use MIME type
		extension = fileObj.type.split("/").pop();
	} else if (fileObj.name) {
		// Fallback: use file name
		extension = fileObj.name.split(".").pop();
	}
	extension = extension || "file";
	return label ? extension.toUpperCase() : extension;
}

function getFileSize(sizeInBytes = 0): string {
	if (sizeInBytes === 0) return "";
	const k = 1024;
	const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
	const i = Math.floor(Math.log(sizeInBytes) / Math.log(k));
	return parseFloat((sizeInBytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

function resizeImage(file: File, { height = 1080 } = {}) {
	return new Promise((res, rej) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = e => {
			const img = new Image();
			img.onload = () => {
				if (Math.max(img.height, img.width) < 500) rej("The image height/width should be more than 500px");
				const el = document.createElement("canvas");
				const ratio = height / img.height;
				let w = (el.width = img.width * ratio);
				let h = (el.height = height);
				const ctx = el.getContext("2d");
				if (!ctx) {
					rej("Failed to get 2D context from canvas");
					return;
				}
				ctx.drawImage(img, 0, 0, w, h);
				res(el.toDataURL("image/jpeg", 0.85));
				reader.onerror = rej;
			};
			if (typeof e.target?.result === "string") {
				img.src = e.target.result;
			} else {
				rej("Failed to read image as data URL");
			}
		};
	});
}

export { delayedCb, getFileExtension, getFileSize, getImageSrc, isImageFile, organizeFilesByType, resizeImage };
